/**
 * /tm-tickets/new — Create a T&M Ticket.
 *
 * Header: project picker, PCO #, work date, ticket #, source, notes.
 * Three repeating sections: Labor lines / Material lines / Equipment lines.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button, IconButton, MenuItem, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { api } from "@/lib/api";
import { tmTicketKeys } from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";
import type { TMTicket } from "@/lib/types";

export const Route = createFileRoute("/_auth/tm-tickets/new")({
  component: TMTicketNewPage,
});

// ---------------------------------------------------------------------------
// Draft line types
// ---------------------------------------------------------------------------
interface DraftLabor {
  uid: string;
  worker_name: string;
  craft: string;
  regular_hours: string;
  ot_hours: string;
  dt_hours: string;
}

interface DraftMaterial {
  uid: string;
  vendor: string;
  description: string;
  category: string;
  amount: string;
  tax: string;
}

interface DraftEquipment {
  uid: string;
  equipment_class: string;
  units: string;
  rate_basis: string;
}

interface DraftSub {
  uid: string;
  subcontractor_id: string;
  description: string;
  raw_cost: string;
  sub_ticket_ref: string;
  cost_code: string;
}

let _uid = 0;
const uid = () => String(++_uid);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function TMTicketNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Header state
  const [projectId, setProjectId] = useState("");
  const [pcoNumber, setPcoNumber] = useState("");
  const [pcoDescription, setPcoDescription] = useState("");
  const [workDate, setWorkDate] = useState("");
  const [ticketNumber, setTicketNumber] = useState("");
  const [notes, setNotes] = useState("");

  // Line state
  const [laborLines, setLaborLines] = useState<DraftLabor[]>([]);
  const [materialLines, setMaterialLines] = useState<DraftMaterial[]>([]);
  const [equipmentLines, setEquipmentLines] = useState<DraftEquipment[]>([]);
  const [subLines, setSubLines] = useState<DraftSub[]>([]);

  const addLabor = () =>
    setLaborLines((prev) => [
      ...prev,
      { uid: uid(), worker_name: "", craft: "", regular_hours: "8", ot_hours: "0", dt_hours: "0" },
    ]);

  const addMaterial = () =>
    setMaterialLines((prev) => [
      ...prev,
      { uid: uid(), vendor: "", description: "", category: "", amount: "", tax: "0" },
    ]);

  const addEquipment = () =>
    setEquipmentLines((prev) => [
      ...prev,
      { uid: uid(), equipment_class: "", units: "1", rate_basis: "daily" },
    ]);

  const addSub = () =>
    setSubLines((prev) => [
      ...prev,
      { uid: uid(), subcontractor_id: "", description: "", raw_cost: "", sub_ticket_ref: "", cost_code: "" },
    ]);

  const create = useMutation({
    mutationFn: async () => {
      const body = {
        project_id: projectId,
        pco_number: pcoNumber,
        pco_description: pcoDescription || null,
        work_date: workDate,
        ticket_number: ticketNumber || null,
        source: "manual",
        status: "draft",
        notes: notes || null,
        labor: laborLines.map(({ uid: _u, ...l }) => ({
          ...l,
          regular_hours: l.regular_hours || "0",
          ot_hours: l.ot_hours || "0",
          dt_hours: l.dt_hours || "0",
        })),
        materials: materialLines.map(({ uid: _u, ...m }) => ({
          ...m,
          amount: m.amount || "0",
          tax: m.tax || "0",
          vendor: m.vendor || null,
          category: m.category || null,
        })),
        equipment: equipmentLines.map(({ uid: _u, ...e }) => ({
          ...e,
          units: e.units || "1",
        })),
        subcontractors: subLines.map(({ uid: _u, ...s }) => ({
          subcontractor_id: s.subcontractor_id,
          description: s.description,
          raw_cost: s.raw_cost || "0",
          sub_ticket_ref: s.sub_ticket_ref || null,
          cost_code: s.cost_code || null,
        })),
      };
      return api<TMTicket>("/api/money/tm-tickets", { method: "POST", body });
    },
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: tmTicketKeys.all });
      toast.success("Ticket created");
      navigate({ to: "/tm-tickets/$ticketId", params: { ticketId: ticket.id } });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const canSubmit = projectId && pcoNumber && workDate;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Typography variant="h5">New T&amp;M Ticket</Typography>
        <div className="flex gap-2">
          <Button variant="outlined" onClick={() => navigate({ to: "/tm-tickets" })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Saving…" : "Create Ticket"}
          </Button>
        </div>
      </div>

      {/* ── Header ── */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Header</Typography>
        <div className="grid grid-cols-2 gap-4">
          <ProjectIdSelect
            label="Project *"
            value={projectId}
            onChange={setProjectId}
            statusFilter={STANDARD_PROJECT_STATUSES}
            size="small"
            fullWidth
            required
            placeholder="Search project code or name…"
          />
          <TextField
            label="PCO # *"
            value={pcoNumber}
            onChange={(e) => setPcoNumber(e.target.value)}
            size="small"
          />
          <TextField
            label="PCO Description"
            value={pcoDescription}
            onChange={(e) => setPcoDescription(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Work Date *"
            type="date"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Ticket #"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            size="small"
          />
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            multiline
            rows={2}
            fullWidth
          />
        </div>
      </div>

      {/* ── Labor Lines ── */}
      <LaborSection lines={laborLines} onChange={setLaborLines} onAdd={addLabor} />

      {/* ── Material Lines ── */}
      <MaterialSection lines={materialLines} onChange={setMaterialLines} onAdd={addMaterial} />

      {/* ── Equipment Lines ── */}
      <EquipmentSection lines={equipmentLines} onChange={setEquipmentLines} onAdd={addEquipment} />

      {/* ── Subcontractor Passthrough Lines ── */}
      <SubSection lines={subLines} onChange={setSubLines} onAdd={addSub} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Labor section
// ---------------------------------------------------------------------------
function LaborSection({
  lines,
  onChange,
  onAdd,
}: {
  lines: DraftLabor[];
  onChange: (v: DraftLabor[]) => void;
  onAdd: () => void;
}) {
  const update = (uid: string, field: keyof DraftLabor, value: string) =>
    onChange(lines.map((l) => (l.uid === uid ? { ...l, [field]: value } : l)));
  const remove = (uid: string) => onChange(lines.filter((l) => l.uid !== uid));

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="subtitle1" fontWeight={600}>Labor Lines</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add Worker</Button>
      </div>
      {lines.length === 0 && (
        <p className="text-sm text-slate-400">No labor lines yet.</p>
      )}
      <div className="space-y-3">
        {lines.map((l) => (
          <div key={l.uid} className="flex gap-2 items-start">
            <TextField label="Worker Name" value={l.worker_name} size="small"
              onChange={(e) => update(l.uid, "worker_name", e.target.value)} sx={{ flex: 2 }} />
            <TextField label="Craft" value={l.craft} size="small"
              onChange={(e) => update(l.uid, "craft", e.target.value)} sx={{ flex: 2 }} />
            <TextField label="Reg Hrs" value={l.regular_hours} size="small" type="number"
              onChange={(e) => update(l.uid, "regular_hours", e.target.value)} sx={{ width: 90 }} />
            <TextField label="OT Hrs" value={l.ot_hours} size="small" type="number"
              onChange={(e) => update(l.uid, "ot_hours", e.target.value)} sx={{ width: 90 }} />
            <TextField label="DT Hrs" value={l.dt_hours} size="small" type="number"
              onChange={(e) => update(l.uid, "dt_hours", e.target.value)} sx={{ width: 90 }} />
            <IconButton size="small" onClick={() => remove(l.uid)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Material section
// ---------------------------------------------------------------------------
function MaterialSection({
  lines,
  onChange,
  onAdd,
}: {
  lines: DraftMaterial[];
  onChange: (v: DraftMaterial[]) => void;
  onAdd: () => void;
}) {
  const update = (uid: string, field: keyof DraftMaterial, value: string) =>
    onChange(lines.map((l) => (l.uid === uid ? { ...l, [field]: value } : l)));
  const remove = (uid: string) => onChange(lines.filter((l) => l.uid !== uid));

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="subtitle1" fontWeight={600}>Material Lines</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add Material</Button>
      </div>
      {lines.length === 0 && <p className="text-sm text-slate-400">No material lines yet.</p>}
      <div className="space-y-3">
        {lines.map((l) => (
          <div key={l.uid} className="flex gap-2 items-start flex-wrap">
            <TextField label="Description" value={l.description} size="small"
              onChange={(e) => update(l.uid, "description", e.target.value)} sx={{ flex: 3, minWidth: 180 }} />
            <TextField label="Vendor" value={l.vendor} size="small"
              onChange={(e) => update(l.uid, "vendor", e.target.value)} sx={{ flex: 2, minWidth: 120 }} />
            <TextField label="Category" value={l.category} size="small"
              onChange={(e) => update(l.uid, "category", e.target.value)} sx={{ flex: 1, minWidth: 100 }} />
            <MoneyField label="Amount" value={l.amount} size="small"
              onChange={(v) => update(l.uid, "amount", v)} sx={{ width: 110 }} />
            <MoneyField label="Tax" value={l.tax} size="small"
              onChange={(v) => update(l.uid, "tax", v)} sx={{ width: 90 }} />
            <IconButton size="small" onClick={() => remove(l.uid)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Equipment section
// ---------------------------------------------------------------------------
function EquipmentSection({
  lines,
  onChange,
  onAdd,
}: {
  lines: DraftEquipment[];
  onChange: (v: DraftEquipment[]) => void;
  onAdd: () => void;
}) {
  const update = (uid: string, field: keyof DraftEquipment, value: string) =>
    onChange(lines.map((l) => (l.uid === uid ? { ...l, [field]: value } : l)));
  const remove = (uid: string) => onChange(lines.filter((l) => l.uid !== uid));

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="subtitle1" fontWeight={600}>Equipment Lines</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add Equipment</Button>
      </div>
      {lines.length === 0 && <p className="text-sm text-slate-400">No equipment lines yet.</p>}
      <div className="space-y-3">
        {lines.map((l) => (
          <div key={l.uid} className="flex gap-2 items-start">
            <TextField label="Equipment Class" value={l.equipment_class} size="small"
              onChange={(e) => update(l.uid, "equipment_class", e.target.value)} sx={{ flex: 3 }} />
            <TextField label="Units" value={l.units} size="small" type="number"
              onChange={(e) => update(l.uid, "units", e.target.value)} sx={{ width: 90 }} />
            <TextField label="Rate Basis" value={l.rate_basis} size="small" select
              onChange={(e) => update(l.uid, "rate_basis", e.target.value)} sx={{ width: 130 }}>
              {["hourly", "daily", "weekly", "monthly"].map((b) => (
                <MenuItem key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</MenuItem>
              ))}
            </TextField>
            <IconButton size="small" onClick={() => remove(l.uid)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcontractor passthrough section
// ---------------------------------------------------------------------------
function SubSection({
  lines,
  onChange,
  onAdd,
}: {
  lines: DraftSub[];
  onChange: (v: DraftSub[]) => void;
  onAdd: () => void;
}) {
  const update = (uid: string, field: keyof DraftSub, value: string) =>
    onChange(lines.map((l) => (l.uid === uid ? { ...l, [field]: value } : l)));
  const remove = (uid: string) => onChange(lines.filter((l) => l.uid !== uid));

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="subtitle1" fontWeight={600}>Subcontractor Passthrough</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add Sub Line</Button>
      </div>
      {lines.length === 0 && (
        <p className="text-sm text-slate-400">No subcontractor passthrough lines yet.</p>
      )}
      <div className="space-y-3">
        {lines.map((l) => (
          <div key={l.uid} className="flex gap-2 items-start flex-wrap">
            <TextField label="Subcontractor ID (UUID)" value={l.subcontractor_id} size="small"
              onChange={(e) => update(l.uid, "subcontractor_id", e.target.value)}
              sx={{ flex: 3, minWidth: 220 }} />
            <TextField label="Description" value={l.description} size="small"
              onChange={(e) => update(l.uid, "description", e.target.value)}
              sx={{ flex: 3, minWidth: 180 }} />
            <MoneyField label="Raw Cost" value={l.raw_cost} size="small"
              onChange={(v) => update(l.uid, "raw_cost", v)} sx={{ width: 120 }} />
            <TextField label="Sub Ticket Ref" value={l.sub_ticket_ref} size="small"
              onChange={(e) => update(l.uid, "sub_ticket_ref", e.target.value)} sx={{ width: 130 }} />
            <TextField label="Cost Code" value={l.cost_code} size="small"
              onChange={(e) => update(l.uid, "cost_code", e.target.value)} sx={{ width: 110 }} />
            <IconButton size="small" onClick={() => remove(l.uid)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
}
